/**
 * Chapter 2, the industry: in numbers (05), what is changing (44), what people
 * search for (42), what they post (43), what everyone does (45).
 */
import type { ContentClass, ContentMix } from "@/types/strategyDocument.type";
import {
  Eyebrow, Foot, Headline, Key, Picture, Slide, SourceLine, V,
  accountName, dayLong, num, rangeLong, slideText,
  type TemplateContext, FigureOr, NO_FIGURE
} from "./kit";
import { StatsPanel, type PanelStat } from "./openingSlides";

/* ── 05 The industry in numbers ───────────────────────────────────────── */

export function IndustryNumbersSlide({ ctx }: { ctx: TemplateContext }) {
  const industry = ctx.doc.industry!;
  const t = slideText(ctx);
  const stats: PanelStat[] = industry.numbers.slice(0, 2).map((n) => ({
    figure: <FigureOr value={num(n.figure)} />,
    label: <V>{n.label}</V>,
    statement: n.statement ? <V>{n.statement}</V> : null,
    source: n.figure.source,
    sourceText: n.figure.source?.label ?? n.evidence[0]?.note ?? null,
  }));
  return (
    <Slide className="sv-two">
      <div className="sv-col sv-two-copy">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="The industry,\n*in numbers.*" />
        {industry.summary ? <V as="p" className="sv-sub">{industry.summary}</V> : null}
        <span className="sv-push" />
        <div className="sv-rows">
          {industry.shifts.slice(0, 2).map((shift, i) => (
            <div key={i} className="sv-row">
              <p className="sv-row-label">{shift.direction === "growing" ? "Growing" : "Shrinking"}</p>
              <V as="p" className="sv-row-text">{shift.text}</V>
            </div>
          ))}
        </div>
        <Foot ctx={ctx} />
      </div>
      <StatsPanel stats={stats} />
    </Slide>
  );
}

/* ── 44 What is changing ──────────────────────────────────────────────── */

const DIRECTION = {
  rising: { arrow: "↑", label: "Rising" },
  declining: { arrow: "↓", label: "Declining" },
  emerging: { arrow: "→", label: "Emerging" },
} as const;

export function IndustryChangesSlide({ ctx }: { ctx: TemplateContext }) {
  const industry = ctx.doc.industry!;
  const rows = ctx.slide.template.id === "industry-changes" ? ctx.slide.template.rows : [];
  const t = slideText(ctx);
  const caption = t.caption;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="What is *changing.*" />
        <div className="sv-changes">
          <div className="sv-ladder">
            {rows.map((i) => {
              const c = industry.changes[i];
              const d = DIRECTION[c.direction];
              return (
                <div key={c.id} className="sv-shift">
                  <span className="sv-shift-mark" data-direction={c.direction} aria-label={d.label}>{d.arrow}</span>
                  <div className="sv-shift-body">
                    <div className="sv-shift-top">
                      <V as="p" className="sv-shift-head">{c.change}</V>
                      <p className="sv-shift-kind">{d.label}{c.kind ? <> · <V>{c.kind}</V></> : null}</p>
                    </div>
                    {c.behaviour ? <V as="p" className="sv-shift-line">{c.behaviour}</V> : null}
                    {c.source?.label || c.evidence[0]?.note ? <V as="p" className="sv-shift-source">{c.source?.label ?? c.evidence[0]?.note}</V> : null}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="sv-changes-pic">
            <Picture ctx={ctx} slot="chapter:2" label="The industry’s picture" className="sv-fill-pic" />
            {caption ? (
              <div className="sv-caption">
                <p>The shift that matters most</p>
                <V as="p">{caption}</V>
              </div>
            ) : null}
          </div>
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

/* ── 42 What people search for ────────────────────────────────────────── */

const ordinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

export function IndustrySearchesSlide({ ctx }: { ctx: TemplateContext }) {
  const industry = ctx.doc.industry!;
  const rows = ctx.slide.template.id === "industry-searches" ? ctx.slide.template.rows : [];
  const t = slideText(ctx);
  const searches = industry.top_searches;
  const ranked = (platform: "TIKTOK" | "YOUTUBE") =>
    searches.filter((s) => s.ranks.some((r) => r.platform === platform && r.rank != null && r.rank <= 10));
  const tiktok = ranked("TIKTOK");
  const youtube = ranked("YOUTUBE");
  const questions = tiktok.filter((s) => s.kind === "question").length;
  const dishes = youtube.filter((s) => s.kind === "dish").length;
  const biggest = [...searches].filter((s) => s.tiktok_hashtag?.views?.value != null).sort((a, b) => (b.tiktok_hashtag!.views!.value ?? 0) - (a.tiktok_hashtag!.views!.value ?? 0))[0];
  const readAt = searches.flatMap((s) => s.ranks.map((r) => r.read_at)).find(Boolean);
  const rankOf = (s: (typeof searches)[number], platform: "TIKTOK" | "YOUTUBE") => s.ranks.find((r) => r.platform === platform);
  return (
    <Slide ink>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="What people\n*search for.*" size={54} lineHeight={60} />
        {t.reading ? <V as="p" className="sv-reading-lg">{t.reading}</V> : null}
        <div className="sv-figures">
          {tiktok.length ? (
            <div className="sv-figure">
              <p className="sv-figure-value"><V>{questions} of {tiktok.length}</V></p>
              <p className="sv-figure-label">top TikTok suggestions are a question</p>
            </div>
          ) : null}
          {youtube.length ? (
            <div className="sv-figure">
              <p className="sv-figure-value"><V>{dishes} of {youtube.length}</V></p>
              <p className="sv-figure-label">top YouTube suggestions name a dish</p>
            </div>
          ) : null}
          {biggest ? (
            <div className="sv-figure">
              <p className="sv-figure-value"><V>{num(biggest.tiktok_hashtag!.views)}</V></p>
              <p className="sv-figure-label">views on the biggest TikTok hashtag, <V>#{biggest.tiktok_hashtag!.tag}</V></p>
            </div>
          ) : (
            <div className="sv-figure">
              <p className="sv-figure-value">{NO_FIGURE}</p>
              <p className="sv-figure-label">Hashtag counts are not read yet.</p>
            </div>
          )}
        </div>
        <span className="sv-push" />
        <div className="sv-table sv-table-ink">
          <div className="sv-table-row sv-table-head">
            <p />
            <p>TikTok · rank and hashtag views</p>
            <p>YouTube · rank</p>
            <p>What it tells us</p>
          </div>
          {rows.map((i) => {
            const s = searches[i];
            const tt = rankOf(s, "TIKTOK");
            const yt = rankOf(s, "YOUTUBE");
            const views = num(s.tiktok_hashtag?.views);
            return (
              <div key={i} className="sv-table-row">
                <V as="p" className="sv-table-key">{s.term}</V>
                <p><V>{tt?.rank ? `${ordinal(tt.rank)}${views ? ` · ${views} views` : ""}` : "not in the top 10"}</V></p>
                <p><V>{yt?.rank ? ordinal(yt.rank) : "not in the top 10"}</V></p>
                <V as="p">{s.meaning ?? ""}</V>
              </div>
            );
          })}
        </div>
        <Foot ctx={ctx} extra={<> · Ranks: the platforms’ own suggestions{readAt ? <>, read <V>{dayLong(readAt)}</V></> : null} · Hashtag views: TikTok’s public counts</>} />
      </div>
    </Slide>
  );
}

/* ── 43 What they post (the donuts) ───────────────────────────────────── */

const CLASSES: ContentClass[] = ["Brand", "Product", "Culture", "Promotion"];
const CLASS_COLOUR: Record<ContentClass, string> = { Brand: "#121212", Product: "#666666", Culture: "#207cf5", Promotion: "#d9d9d9" };

const shareOf = (mix: ContentMix | null | undefined, c: ContentClass) => mix?.classes.find((x) => x.class === c)?.share_pct ?? 0;
const dominant = (mix: ContentMix) => [...mix.classes].sort((a, b) => b.share_pct - a.share_pct)[0];

export function Donut({ mix, size, ring }: { mix: ContentMix; size: number; ring: number }) {
  const r = (size - ring) / 2;
  const c = 2 * Math.PI * r;
  const lengths = CLASSES.map((cls) => (shareOf(mix, cls) / 100) * c);
  return (
    <svg className="sv-donut-ring" viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden>
      {CLASSES.map((cls, i) => {
        const len = lengths[i];
        const at = lengths.slice(0, i).reduce((a, b) => a + b, 0);
        return <circle key={cls} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={CLASS_COLOUR[cls]} strokeWidth={ring} strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-at} transform={`rotate(-90 ${size / 2} ${size / 2})`} />;
      })}
    </svg>
  );
}

function DonutCard({ name, mix, sample, set, collabTop }: { name: string; mix: ContentMix; sample: string; set?: boolean; collabTop?: boolean }) {
  const top = dominant(mix);
  const size = set ? 150 : 128;
  return (
    <div className={`sv-mix-card${set ? " sv-mix-set" : ""}`}>
      <div className="sv-donut" style={{ width: size, height: size }}>
        <Donut mix={mix} size={size} ring={set ? 28 : 24} />
        <div className="sv-donut-centre">
          <V as="p" className="sv-donut-share">{top.share_pct}%</V>
          <V as="p" className="sv-donut-class">{top.class}</V>
        </div>
      </div>
      <V as="p" className="sv-mix-name">{name}</V>
      <p className="sv-mix-sample"><V>{sample}</V></p>
      {mix.collaborations ? (
        <p className={`sv-mix-collab${collabTop ? " sv-mix-collab-top" : ""}`}><V>{mix.collaborations.share_pct}%</V> with a creator or a brand</p>
      ) : null}
    </div>
  );
}

const SET_WORDS = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

export function IndustryMixSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const t = slideText(ctx);
  const accounts = [
    ...(doc.brand?.content_mix ? [{ key: "brand", name: ctx.brand, mix: doc.brand.content_mix }] : []),
    ...(doc.competitors?.set ?? []).filter((c) => c.content_mix && c.status !== "dormant").map((c) => ({ key: c.id, name: c.name, mix: c.content_mix! })),
  ];
  const summary = doc.competitors?.mix_summary;
  const collabTop = Math.max(...accounts.map((a) => a.mix.collaborations?.share_pct ?? 0));
  const window = accounts.find((a) => a.mix.sample?.window_start)?.mix.sample;
  const culture = [...accounts].sort((a, b) => shareOf(b.mix, "Culture") - shareOf(a.mix, "Culture"))[0];
  const promotion = [...accounts].sort((a, b) => shareOf(a.mix, "Promotion") - shareOf(b.mix, "Promotion"))[0];
  const product = summary ? shareOf(summary, "Product") : null;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="What the category *posts.*" />
        <div className="sv-mix">
          <div className="sv-legend-row">
            <p className="sv-key">Share of posts{window?.window_start ? <>, <V>{rangeLong(window.window_start, window.window_end)}</V></> : null} · one class per post</p>
            <span className="sv-push" />
            {CLASSES.map((c) => (
              <span key={c} className="sv-legend"><i style={{ background: CLASS_COLOUR[c] }} />{c}</span>
            ))}
          </div>
          <div className="sv-mix-row">
            {accounts.map((a) => (
              <DonutCard key={a.key} name={a.name} mix={a.mix} sample={a.mix.sample?.posts ? `${a.mix.sample.posts} posts` : ""} collabTop={!!a.mix.collaborations && a.mix.collaborations.share_pct === collabTop && collabTop > 0} />
            ))}
            {summary ? <DonutCard name="The set" mix={summary} sample={`average of ${SET_WORDS[summary.sample?.accounts ?? 0] || summary.sample?.accounts || ""}`} set /> : null}
          </div>
          <div className="sv-mix-figures">
            {product != null ? (
              <div className="sv-mix-figure"><V as="p">{product}%</V><p>of the set’s posts are Product, the category’s default.</p></div>
            ) : null}
            {culture ? (
              <div className="sv-mix-figure"><V as="p">{shareOf(culture.mix, "Culture")}%</V><p>of <V>{culture.name}</V>’s posts are Culture, the highest in the set.</p></div>
            ) : null}
            {promotion ? (
              <div className="sv-mix-figure"><V as="p">{shareOf(promotion.mix, "Promotion")}%</V><p>of <V>{promotion.name}</V>’s posts are Promotion, the lowest.</p></div>
            ) : null}
          </div>
          <SourceLine source={summary?.source} className="sv-source-plain">{summary?.source?.label ?? "Every post of the last 90 days on the required platforms, read by Creo. Collaboration = a creator or another brand on the post."}</SourceLine>
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

/* ── 45 What everyone does ────────────────────────────────────────────── */

export function IndustryConventionsSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const industry = doc.industry!;
  const rows = ctx.slide.template.id === "industry-conventions" ? ctx.slide.template.rows : [];
  const t = slideText(ctx);
  const setSize = 1 + (doc.competitors?.set ?? []).filter((c) => c.status !== "dormant").length;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="What *everyone* does." />
        <div className="sv-conventions">
          <div className="sv-conv-head">
            <Key>Accounts</Key>
            <Key>The habit</Key>
            <Key>Who does it</Key>
          </div>
          {rows.map((i) => {
            const c = industry.conventions[i];
            const all = c.who.length >= setSize;
            return (
              <div key={c.id} className="sv-conv">
                <p className="sv-conv-count" data-all={all || undefined}><V>{c.who.length} of {setSize}</V></p>
                <div className="sv-conv-text">
                  <V as="p" className="sv-conv-head-line">{c.convention}</V>
                  {c.source?.label ? <V as="p" className="sv-conv-source">{c.source.label}</V> : null}
                </div>
                <div className="sv-conv-who">
                  {c.who.map((who) => (
                    <V key={who} className={`sv-pill${who === "brand" ? " sv-pill-brand" : ""}`}>{accountName(doc, ctx.brand, who)}</V>
                  ))}
                </div>
              </div>
            );
          })}
          {t.reading ? (
            <div className="sv-closing">
              <Key>What we will not repeat</Key>
              <V as="p">{t.reading}</V>
            </div>
          ) : null}
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

